import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import useRosterFileHistoryEvents from '../../../rosterTools/hooks/useRosterFileHistoryEvents';
import {
  buildActivityForYear,
  getAvailableActivityYears,
} from '../../../rosterTools/statistics/rosterHistoryChartData';
import '../../../rosterTools/statistics/registerRosterStatCharts';
import {
  DASHBOARD_CHART_LINE,
  dashboardLineChartOptions,
} from './dashboardChartTheme';

function pickDefaultActivityYear(availableYears) {
  if (!availableYears.length) return new Date().getFullYear();
  const current = new Date().getFullYear();
  if (availableYears.includes(current)) return current;
  return availableYears[0];
}

export default function DashboardActivityChart({
  handleId,
  scope = 'cluster',
  enabled = false,
  rosterDatasetRevision = 0,
  title = null,
}) {
  const { eventsLoading, eventsError, visibleEvents } = useRosterFileHistoryEvents({
    handleId,
    scope,
    enabled,
    rosterDatasetRevision,
  });

  const availableYears = useMemo(
    () => getAvailableActivityYears(visibleEvents),
    [visibleEvents],
  );

  const [selectedYear, setSelectedYear] = useState(() =>
    pickDefaultActivityYear(availableYears),
  );

  useEffect(() => {
    setSelectedYear((prev) => {
      if (prev != null && availableYears.includes(prev)) return prev;
      return pickDefaultActivityYear(availableYears);
    });
  }, [availableYears]);

  const activityByMonth = useMemo(() => {
    if (selectedYear == null) return { labels: [], values: [] };
    return buildActivityForYear(visibleEvents, selectedYear);
  }, [visibleEvents, selectedYear]);

  const selectId = `dashboard-activity-year-${handleId ?? 'x'}`;
  const chartWrapRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    const el = chartWrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(() => {
      chartRef.current?.resize();
    });
    observer.observe(el);

    const gridItem = el.closest('.react-grid-item');
    if (gridItem) {
      observer.observe(gridItem);
    }

    return () => observer.disconnect();
  }, [eventsLoading, eventsError]);

  const toolbar = (
    <div className="member-dashboard__chart-toolbar">
      <label
        htmlFor={selectId}
        className="member-dashboard__chart-toolbar-label"
      >
        Year
      </label>
      <select
        id={selectId}
        className="member-dashboard__chart-year-select"
        value={selectedYear ?? ''}
        onChange={(e) => setSelectedYear(Number(e.target.value))}
        disabled={eventsLoading || availableYears.length === 0}
        aria-label="Activity year"
      >
        {availableYears.map((year) => (
          <option key={year} value={year}>
            {year}
          </option>
        ))}
      </select>
    </div>
  );

  const body = eventsLoading ? (
    <p className="member-dashboard__panel-placeholder">Loading…</p>
  ) : eventsError ? (
    <p className="member-dashboard__panel-placeholder">{eventsError}</p>
  ) : (
    <div
      ref={chartWrapRef}
      className="member-dashboard__chart member-dashboard__chart--line"
    >
      <Line
        ref={chartRef}
        data={{
          labels: activityByMonth.labels,
          datasets: [
            {
              label: 'Count',
              data: activityByMonth.values,
              borderColor: DASHBOARD_CHART_LINE.borderColor,
              backgroundColor: DASHBOARD_CHART_LINE.backgroundColor,
              pointRadius: 2,
              tension: 0.25,
              fill: true,
            },
          ],
        }}
        options={dashboardLineChartOptions}
      />
    </div>
  );

  if (title) {
    return (
      <>
        <div
          className="display-none"
          data-resource="private.navbar.personalhub.activity"
          data-action="read"
          data-route="/api/user/roster-file-history-events"
          data-description="Personal hub — activity chart"
        />

        <div className="member-dashboard__panel-card-head member-dashboard__activity-card-head">
          <h2 className="member-dashboard__panel-title">{title}</h2>
          {toolbar}
        </div>

        <div className="member-dashboard__panel-card-body">{body}</div>
      </>
    );
  }

  return (
    <>
      <div
        className="display-none"
        data-resource="private.navbar.personalhub.activity"
        data-action="read"
        data-route="/api/user/roster-file-history-events"
        data-description="Personal hub — activity chart"
      />

      {toolbar}
      {body}
    </>
  );
}
